import { useId, useState } from "react";
import { Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import {
  useGoogleAuth,
  useGithubAuth,
  useMicrosoftAuth,
  useResendVerificationEmail,
  useRequestSignup,
  useInitiateSSO,
} from "../../../hooks/useAPI";
import {
  AuthShell,
  AuthBanner,
  AuthInput,
  AuthButton,
  AuthInfoCard,
  OAuthButtonRow,
  Crossfade,
  ResendButton,
} from "./_primitives";

type SignUpView = "form" | "verification";

const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

export function SignUpPage() {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  // Defense-in-depth honeypot — bots auto-fill every visible field; real
  // users never see this one (visually hidden + autoComplete="off"). If
  // it has any value at submit time we silently drop the request and
  // surface a generic message, so spammers can't distinguish the
  // honeypot from a successful create.
  const [website, setWebsite] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [signUpView, setSignUpView] = useState<SignUpView>("form");
  const termsId = useId();

  const requestSignup = useRequestSignup();
  const googleAuth = useGoogleAuth();
  const githubAuth = useGithubAuth();
  const microsoftAuth = useMicrosoftAuth();
  const resendVerification = useResendVerificationEmail();
  const initiateSSO = useInitiateSSO();

  const isAuthLoading =
    requestSignup.isPending ||
    googleAuth.isPending ||
    githubAuth.isPending ||
    microsoftAuth.isPending ||
    initiateSSO.isPending;

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against rapid double-submit via Enter-key spam.
    if (isAuthLoading) return;
    setError("");

    // Honeypot — bots fill every visible field; humans never touch this.
    // Silently treat as a successful submit so the spammer can't probe.
    if (website.trim()) {
      setSignUpView("verification");
      return;
    }

    if (!fullName.trim()) { setError(t("signup.errors.fullNameRequired")); return; }
    if (!company.trim()) { setError(t("signup.errors.companyRequired")); return; }
    if (!email || !isValidEmail(email)) { setError(t("signup.errors.emailRequired")); return; }
    if (!termsAccepted) {
      setError(t("signup.errors.termsRequired"));
      return;
    }

    try {
      await requestSignup.mutateAsync({
        name: fullName.trim(),
        email: email.trim().toLowerCase(),
        organizationName: company.trim(),
      });
      setSignUpView("verification");
      toast.success(t("signup.toasts.accountCreated"), { description: t("signup.toasts.accountCreatedDescription") });
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      const code = (err as { code?: string } | undefined)?.code || "";
      const lowered = (code + " " + message).toLowerCase();
      if (
        code === "ALREADY_EXISTS" ||
        code === "EMAIL_EXISTS" ||
        lowered.includes("already exists") ||
        lowered.includes("already registered")
      ) {
        setError(t("signup.errors.alreadyExists"));
      } else if (code === "ORG_NAME_TAKEN" || (lowered.includes("organization") && lowered.includes("taken"))) {
        setError(t("signup.errors.orgNameTaken"));
      } else {
        setError(t("signup.errors.generic"));
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email || !isValidEmail(email)) {
      toast.error(t("signup.resendEnterEmail"));
      return;
    }
    try {
      await resendVerification.mutateAsync(email);
      toast.success(t("signup.toasts.verificationResent"), { description: t("signup.toasts.verificationResentDescription", { email }) });
    } catch {
      toast.error(t("signup.toasts.verificationResentFailed"));
    }
  };

  const handleChangeEmail = () => {
    setSignUpView("form");
    setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>('input[name="email"]');
      el?.focus();
      el?.select();
    }, 0);
  };

  const handleSSOSignUp = () => {
    setError("");
    if (!email || !isValidEmail(email)) {
      setError(t("signup.errors.ssoEmailRequired"));
      return;
    }
    initiateSSO.mutate(
      { email: email.trim().toLowerCase() },
      {
        onSuccess: (data) => { window.location.href = data.authUrl; },
        onError: (err) => {
          const msg = err instanceof Error ? err.message.toLowerCase() : "";
          if (msg.includes("not configured") || msg.includes("no sso")) {
            setError(t("signup.errors.ssoNotConfigured"));
          } else {
            setError(t("signup.errors.ssoGeneric"));
          }
        },
      },
    );
  };

  // No brand panel — pure utility-style auth surface (Mercury / Linear /
  // Vercel / Plaid / Cloudflare convention). Per-page persuasion lives
  // on the marketing site, not on the auth surface.

  const headerAction = (
    <span className="text-sm text-[var(--muted-foreground)]">
      {t("signup.headerActionPrefix")}{" "}
      <Link
        to="/login"
        className="text-[var(--foreground)] hover:text-[var(--muted-foreground)] transition-colors motion-reduce:transition-none font-medium underline underline-offset-2"
      >
        {t("signup.headerActionLink")}
      </Link>
    </span>
  );

  return (
    <AuthShell
      pageTitle={t("signup.pageTitle")}
      pageDescription={t("signup.pageDescription")}
      headerAction={headerAction}
      wide={signUpView === "form"}
    >
      <Crossfade stateKey={signUpView} focusOnEnter="h1">
        {signUpView === "verification" && (
          <div className="space-y-6">
            <h1 className="sr-only">{t("signup.verifyAriaLabel")}</h1>
            <AuthInfoCard icon={Mail} title={t("signup.verificationCardTitle")}>
              <p>
                <Trans
                  i18nKey="signup.verificationBody1"
                  values={{ email }}
                  components={[<span key="email" className="font-medium text-[var(--foreground)]" />]}
                />
              </p>
              <p className="mt-2">
                {t("signup.verificationBody2")}
              </p>
            </AuthInfoCard>

            <div className="flex items-center gap-4 flex-wrap">
              <ResendButton onSend={handleResendVerification} label={t("signup.resendEmail")} />
              <span className="text-sm text-[var(--muted-foreground)]" aria-hidden="true">·</span>
              <button
                type="button"
                onClick={handleChangeEmail}
                className="auth-tertiary"
              >
                {t("signup.wrongEmail")}
              </button>
            </div>

            <div className="pt-4 border-t border-[var(--border)] text-center">
              <p className="text-sm text-[var(--muted-foreground)]">
                {t("signup.alreadyAccountPrefix")}{" "}
                <Link
                  to="/login"
                  className="text-[var(--foreground)] hover:text-[var(--muted-foreground)] transition-colors motion-reduce:transition-none font-medium underline underline-offset-2"
                >
                  {t("signup.alreadyAccountLink")}
                </Link>
              </p>
            </div>
          </div>
        )}

        {signUpView === "form" && (
          <div className="space-y-8">
            <header className="text-center">
              <h1 className="editorial-display">
                {t("signup.title")}
              </h1>
              <p className="editorial-subtitle">
                {t("signup.subtitle")}
              </p>
            </header>

            {error && <AuthBanner variant="error">{error}</AuthBanner>}

            <div className="auth-split">
              {/* LEFT — name / company / email + terms + continue */}
              <form onSubmit={handleSignUp} className="space-y-4" noValidate>
                {/* Honeypot — visually hidden from sighted + SR users via
                    aria-hidden + tabIndex=-1 + sr-only positioning. Bots
                    that auto-fill every input will populate this field
                    and trigger the silent-success path in handleSignUp. */}
                <div
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}
                >
                  <label htmlFor="signup-website">{t("signup.honeypotLabel")}</label>
                  <input
                    id="signup-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>

                <AuthInput
                  label={t("signup.fullNameLabel")}
                  type="text"
                  name="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t("signup.fullNamePlaceholder")}
                  autoComplete="name"
                  autoCapitalize="words"
                  disabled={isAuthLoading}
                />

                <AuthInput
                  label={t("signup.companyLabel")}
                  type="text"
                  name="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder={t("signup.companyPlaceholder")}
                  autoComplete="organization"
                  autoCapitalize="words"
                  disabled={isAuthLoading}
                />

                <AuthInput
                  label={t("signup.emailLabel")}
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("signup.emailPlaceholder")}
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  disabled={isAuthLoading}
                />

                {/* Terms — real <input type=checkbox> for SR role+state. */}
                <div className="flex items-start gap-2.5 pt-1">
                  <label htmlFor={termsId} className="flex items-start gap-2.5 cursor-pointer select-none has-[:disabled]:cursor-not-allowed">
                    <span className="relative mt-0.5 flex-shrink-0 inline-flex">
                      <input
                        id={termsId}
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        disabled={isAuthLoading}
                        className="peer w-4 h-4 appearance-none border bg-[var(--surface-1)] border-[var(--border)] checked:bg-[var(--foreground)] checked:border-[var(--foreground)] disabled:opacity-50 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-2 transition-colors motion-reduce:transition-none"
                      />
                      <Check
                        aria-hidden="true"
                        className="absolute inset-0 w-2.5 h-2.5 m-auto text-[var(--background)] opacity-0 peer-checked:opacity-100 pointer-events-none"
                      />
                    </span>
                    <span className="text-xs text-[var(--muted-foreground)]" style={{ lineHeight: 1.5 }}>
                      {t("signup.termsBefore")}{" "}
                      <Link to="/legal/terms" className="underline underline-offset-2 hover:text-[var(--foreground)]" target="_blank" rel="noopener noreferrer">
                        {t("signup.termsOfService")}
                      </Link>{" "}
                      {t("signup.termsAnd")}{" "}
                      <Link to="/legal/dpa" className="underline underline-offset-2 hover:text-[var(--foreground)]" target="_blank" rel="noopener noreferrer">
                        {t("signup.dpa")}
                      </Link>
                      .
                    </span>
                  </label>
                </div>

                <AuthButton
                  type="submit"
                  variant="primary"
                  fullWidth
                  loading={requestSignup.isPending}
                  loadingLabel={t("signup.submitLoading")}
                  disabled={isAuthLoading}
                >
                  {t("signup.submit")}
                </AuthButton>
              </form>

              {/* Divider */}
              <div className="auth-split-divider" aria-hidden="true" />

              {/* RIGHT — OAuth + SSO */}
              <div className="space-y-3">
                <OAuthButtonRow
                  onGoogle={() => googleAuth.mutate()}
                  onGithub={() => githubAuth.mutate()}
                  onMicrosoft={() => microsoftAuth.mutate()}
                  onSSO={handleSSOSignUp}
                  loading={{
                    google: googleAuth.isPending,
                    github: githubAuth.isPending,
                    microsoft: microsoftAuth.isPending,
                    sso: initiateSSO.isPending,
                    any: isAuthLoading,
                  }}
                  verb="sign up"
                />
                <p className="auth-helper">
                  {t("signup.ssoHelper")}
                </p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-[var(--muted-foreground)]">
                {t("signup.enterprisePrefix")}{" "}
                <Link to="/contact#demo" className="text-[var(--foreground)] hover:text-[var(--muted-foreground)] transition-colors motion-reduce:transition-none font-medium underline underline-offset-2">
                  {t("signup.enterpriseLink")}
                </Link>
              </p>
            </div>
          </div>
        )}
      </Crossfade>
    </AuthShell>
  );
}
